import { usePersonStore } from '~/stores/person';
import type { BaseAPIResponse } from '~/types/api';
import type { ConversationMessage } from '~/types/companies/conversations';
import type { CompanyExposure, Exposure, PersonExposure } from '~/types/person/details';
import type { PotentialExposuresResponseData, SuppressionRecord, SuppressionsApiResponse } from '~/types/person/vault';
import { ApiError } from '~/utils/errors';

// Pure API service for exposure-related operations - no state management
export const exposuresService = {
    async getExposure(personId: number, exposureId: string): Promise<Exposure> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<any>>(
            nuxtApp.$api.routes.people.exposures.exposure(personId, exposureId)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch exposure details', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No exposure details received', 404);
        }

        return data.data;
    },

    async getExposures(personId: number, limit: number = 50): Promise<PersonExposure[]> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<PersonExposure[]>>(
            nuxtApp.$api.routes.people.exposures.list(personId, limit)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch exposures', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No exposures received', 404);
        }

        return data.data;
    },

    async getPotentialExposures(personId: number): Promise<PotentialExposuresResponseData> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<PotentialExposuresResponseData>>(
            nuxtApp.$api.routes.people.exposures.data_exposed(personId),
            {
                method: 'GET',
            }
        );
        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch potential exposures', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No potential exposures data received', 404);
        }
        return data.data;
    },

    async updateExposure(type: string, value: any, isMyExposure: boolean): Promise<SuppressionRecord> {
        const nuxtApp = useNuxtApp();
        const personStore = usePersonStore();

        if (!personStore.person?.id) {
            throw new ApiError('No person found', 404);
        }

        try {
            const { data, status, message } = await usePbApi<BaseAPIResponse<SuppressionRecord>>(
                nuxtApp.$api.routes.people.exposures.suppressions.update_suppressions(personStore.person.id),
                {
                    method: 'PUT',
                    body: {
                        type: type,
                        data: value,
                        me: isMyExposure,
                    },
                }
            );

            // The v2 backend returns the saved record in the 201 body; an empty
            // body means the suppression did not persist and must surface as an error.
            if (status !== 201 || !data) {
                throw new ApiError(message || 'Failed to update exposure', status);
            }

            return data.data;
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getSuppressedRecords(): Promise<SuppressionRecord[]> {
        const nuxtApp = useNuxtApp();
        const personStore = usePersonStore();

        if (!personStore.person?.id) {
            throw new ApiError('No person found', 404);
        }

        const { data, status, message } = await usePbApi<BaseAPIResponse<SuppressionsApiResponse>>(
            nuxtApp.$api.routes.people.exposures.suppressions.get_suppressions(personStore.person.id),
            {
                method: 'GET',
            }
        );

        if (status !== 200) {
            throw new ApiError('Could not fetch suppressed records', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No suppressed records data received', 404);
        }

        const parsedRecords: SuppressionRecord[] = [];

        // Process each record - backend now handles JSON decoding
        for (const record of data.data as any) {
            // If relatives, skip for now (as per original logic)
            if (record.type === 'relatives') {
                continue;
            }

            // Backend already decodes JSON, so we don't need to parse again
            // But keep backward compatibility in case data is still a string
            let processedData = record.data;
            if (typeof record.data === 'string' && record.data.match(/^{.*}$/)) {
                try {
                    processedData = JSON.parse(record.data);
                } catch (e) {
                    // If parsing fails, keep as string
                    processedData = record.data;
                }
            }

            parsedRecords.push({
                ...record,
                data: processedData,
            });
        }

        return parsedRecords;
    },

    async deleteSuppressedRecord(suppressionId: number): Promise<void> {
        const nuxtApp = useNuxtApp();
        const personStore = usePersonStore();

        if (!personStore.person?.id) {
            throw new ApiError('No person found', 404);
        }

        try {
            const { status, message } = await usePbApi(
                nuxtApp.$api.routes.people.exposures.suppressions.remove_suppression(personStore.person.id, suppressionId),
                {
                    method: 'DELETE',
                }
            );

            if (status !== 200) {
                throw new ApiError(message || 'Failed to delete suppressed record', status);
            }
        } catch (error: any) {
            throw ApiError.fromFetchError(error);
        }
    },

    async getExposureDetails(personId: number): Promise<CompanyExposure[]> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<CompanyExposure[]>>(
            nuxtApp.$api.routes.people.exposures.detail(personId)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch exposure details', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No exposure details data received', 404);
        }

        return data.data;
    },

    async getHistoryExport(personId: number): Promise<{url: string}> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<{url: string}>(nuxtApp.$api.routes.people.exposures.history_export(personId));

        if (status !== 200) {
            throw new ApiError(message || 'Failed to export history', status);
        }

        if (!data?.url) {
            throw new ApiError('No download URL received', 404);
        }

        return data;
    },

    async requestDeletion(companyId: number): Promise<void> {
        const nuxtApp = useNuxtApp();
        const personStore = usePersonStore();

        const { status, message } = await usePbApi(
            nuxtApp.$api.routes.people.companies.untrust_company(personStore.person?.id || 0, companyId),
            {
                method: 'POST',
                body: {
                    delete: true,
                },
            }
        );

        if (status !== 201) {
            throw new ApiError(message || 'Failed to request deletion', status);
        }
    },

    async getExposureHistory(personId: number, exposureId: string): Promise<ConversationMessage[]> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<ConversationMessage[]>>(
            nuxtApp.$api.routes.people.exposures.exposure_history(personId, exposureId)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch exposure history', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No history data received', 404);
        }

        return data.data;
    },

    async getCompanyHistory(personId: number, companyId: number): Promise<ConversationMessage[]> {
        const nuxtApp = useNuxtApp();

        // Backend now returns same format as getExposureHistory (ConversationMessageV3Resource)
        const { data, status, message } = await usePbApi<BaseAPIResponse<ConversationMessage[]>>(
            nuxtApp.$api.routes.people.exposures.company_history(personId, companyId)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch company history', status || 500);
        }

        // Handle empty response - return empty array instead of throwing error
        if (!data?.data) {
            return [];
        }

        return data.data;
    },

    /**
     * Get digital footprint stats for a given time period
     * @param personId - The person ID
     * @param months - Number of months (3, 6, or 9)
     */
    async getDigitalFootprintStats(personId: number, months: number = 6): Promise<{
        period: { months: number; start_date: string; end_date: string };
        stats: {
            scans_performed: number;
            exposures_found: number;
            removals_requested: number;
            removals_completed: number;
        };
        all_time: {
            scans_performed: number;
            exposures_found: number;
            removals_requested: number;
            removals_completed: number;
        };
    }> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<any>(
            nuxtApp.$api.routes.people.exposures.digital_footprint(personId, months)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch digital footprint stats', status || 500);
        }

        if (!data?.data) {
            throw new ApiError('No digital footprint data received', 404);
        }

        return data.data;
    },

    /**
     * Get exposures for a specific company
     * Returns detailed exposure data including screenshots and detected data.
     * The response mixes detail and remediation fields; callers pick their view
     * via the type parameter (assertion-based, like every usePbApi call).
     */
    async getExposuresForCompany<T = CompanyExposureDetail>(personId: number, companyId: number): Promise<T[]> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<T[]>>(
            nuxtApp.$api.routes.people.companies.exposures(personId, companyId)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch company exposures', status || 500);
        }

        return data?.data || [];
    },

    /**
     * Get recent people search/affiliate exposures for a person
     * Returns exposures sorted by most recent activity (created_at, in_progress_at, closed_at)
     */
    async getRecentExposures(personId: number, limit: number = 6): Promise<RecentExposure[]> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<BaseAPIResponse<RecentExposure[]>>(
            nuxtApp.$api.routes.people.exposures.recent(personId, limit)
        );

        if (status !== 200) {
            throw new ApiError(message || 'Failed to fetch recent exposures', status || 500);
        }

        return data?.data || [];
    },

    /**
     * Submit a custom removal request for a URL
     * @param personId - The person ID
     * @param url - The URL where the person's data was found
     * @returns Custom removal response with exposure info
     */
    async submitCustomRemoval(personId: number, url: string): Promise<CustomRemovalResponse> {
        const nuxtApp = useNuxtApp();

        const { data, status, message } = await usePbApi<CustomRemovalResponse>(
            nuxtApp.$api.routes.people.exposures.custom_removal(personId),
            {
                method: 'POST',
                body: { url },
            }
        );

        if (status !== 200) {
            // Check if it's a duplicate exposure (already exists)
            if (status === 409 && data?.already_exists) {
                throw new CustomRemovalError(
                    message || 'A removal request for this URL is already in progress.',
                    status,
                    false, // not a license upgrade issue
                    data.exposure_id,
                    data.company,
                    true // already exists
                );
            }
            // Check if it's a license upgrade required error
            if (status === 403 && data?.requires_upgrade) {
                throw new CustomRemovalError(
                    message || 'License upgrade required',
                    status,
                    true,
                    data.exposure_id,
                    data.company
                );
            }
            throw new ApiError(message || 'Failed to submit custom removal request', status || 500);
        }

        return data as CustomRemovalResponse;
    },
};

// Type for company exposure detail response (used in data brokers expansion)
export interface CompanyExposureDetail {
    exposure_id: string;
    company_id: number;
    person_id: number;
    status: string;
    profile_url: string | null;
    created_at: string;
    in_progress_at: string | null;
    closed_at: string | null;
    last_action_at: string;
    detected_data: string[];
    timeline: {
        exposure_found: string | null;
        request_sent: string | null;
        verified_clean: string | null;
    };
    data: {
        name?: string | null;
        age?: string | null;
        date_of_birth?: string | null;
        addresses?: any[];
        phones?: string[];
        emails?: string[];
        relatives?: any[];
        location?: string | null;
        education?: string | null;
        occupation?: string | null;
        url?: string | null;
        screenshot_url?: string | null;
    } | null;
}

// Type for custom removal response
export interface CustomRemovalResponse {
    success: boolean;
    message: string;
    exposure_id?: string;
    requires_upgrade?: boolean;
    already_exists?: boolean;
    company?: {
        id: number;
        name: string;
        type: string | null;
    };
}

// Custom error class for custom removal that includes additional data
export class CustomRemovalError extends Error {
    status: number;
    requiresUpgrade: boolean;
    alreadyExists: boolean;
    exposureId?: string;
    company?: { id: number; name: string; type: string | null };

    constructor(
        message: string,
        status: number,
        requiresUpgrade: boolean = false,
        exposureId?: string,
        company?: { id: number; name: string; type: string | null },
        alreadyExists: boolean = false
    ) {
        super(message);
        this.name = 'CustomRemovalError';
        this.status = status;
        this.requiresUpgrade = requiresUpgrade;
        this.alreadyExists = alreadyExists;
        this.exposureId = exposureId;
        this.company = company;
    }
}

// Type for recent exposure response
export interface RecentExposure {
    exposure_id: string;
    company_id: number;
    status: string;
    activity_type: 'discovered' | 'in_progress' | 'closed';
    activity_date: string;
    created_at: string;
    in_progress_at: string | null;
    closed_at: string | null;
    profile_url: string | null;
    data: {
        first_name?: string | null;
        middle_name?: string | null;
        last_name?: string | null;
        name?: string | null;
        emails?: string[];
        phones?: string[];
        addresses?: { line1?: string; city?: string; state?: string; postal_code?: string }[];
        aka?: string[];
        relatives?: { first_name?: string; last_name?: string; name?: string; age?: string }[];
        age?: string | null;
        date_of_birth?: string | null;
        screenshot_full_path?: string | null;
        url?: string | null;
        search_url?: string | null;
        record_id?: string | null;
    } | null;
    company: {
        id: number;
        name: string;
        slug: string;
        type: string;
        logo: string | null;
    } | null;
}
